import { NOTIFICATION_INTERVAL_DELAY } from '@/constants';
import Vue from 'vue';
import Router from 'vue-router';
import routes from '@/router/routes';
import store from '@/store/store';
import apiNotifications from '@/api/notifications';
import gtm from '@/gtm';
import tracking from '@/tracking';

Vue.use(Router);

let isInited = false;

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: routes,
    scrollBehavior(to, from, savedPosition) {
        return { x: 0, y: 0 };
    }
});

function getNotifications() {
    return apiNotifications
        .get()
        .then(response => {
            store.dispatch('view/setNotifications', response.data.items);
            store.dispatch('view/setVersion', response.data.version);

            // メンテナンスモードの場合
            if (store.getters['view/isMaintenance']) {
                // ログインしているかつ謹告のlogoutオプションがtrueの場合は強制的にログアウトする
                if (
                    router.app.auth.isAuthenticated &&
                    store.getters['view/maintenance'].logout
                ) {
                    router.app.auth.logout();
                    return;
                }
                // 謹告2回目以降の取得時にログインページにいない場合はログインページに遷移させる
                // 謹告1回目取得時はまだ$route.nameがnullなので、ここでは何もせずbeforeEach関数内で遷移先を操作する
                if (
                    router.app.$route.name &&
                    router.app.$route.name !== 'login'
                ) {
                    router.push({ name: 'login', replace: true });
                }
            }
        })
        .catch(error => {});
}

function getUserData() {
    return router.app.auth
        .getUserData()
        .then(response => {
            store.dispatch('user/setStatus', {
                agentCode: response.data.user_metadata.agentCode,
                company: response.data.user_metadata.company,
                username: response.data.user_metadata.username,
                isAdmin: response.data.user_metadata.isAdmin,
                isAgency: response.data.user_metadata.isAgency,
                isTester: response.data.user_metadata.isTester,
                isTrial: response.data.user_metadata.isTrial
            });
        })
        .catch(error => {});
}

function handleNext(to, from, next) {
    // 同じページに遷移しようとしている場合はキャンセルする
    if (from.name === to.name) {
        stopLoading();
        updateMeta(from);
        next(false);
    } else {
        next(to);
    }
}

function go404(from, next) {
    if (router.app.auth.isAuthenticated) {
        // ログインしている場合はサイト一覧に遷移
        handleNext({ name: 'projects', replace: true }, from, next);
    } else {
        // ログインしていない場合はサービスサイトの404に遷移
        location.href = router.app.$constants.serviceSiteUrl + '/404';
    }
}

function startLoading(from, to) {
    // 子ページから子ページへの遷移の場合はローディングを表示しない
    if (
        from.matched.some(record => record.meta.isChild) &&
        to.matched.some(record => record.meta.isChild)
    ) {
        return;
    }

    // fromもtoもサイドバーがある場合は、main部分のみローディングを表示する
    if (
        (from.matched.some(record => record.meta.hasSide) &&
            to.matched.some(record => record.meta.hasSide)) ||
        (from.matched.some(record => record.meta.hasSideInfo) &&
            to.matched.some(record => record.meta.hasSideInfo))
    ) {
        store.dispatch('view/setLoadingMain', true);
    } else {
        store.dispatch('view/setLoading', true);
    }
}

function stopLoading() {
    store.dispatch('view/setLoading', false);
    store.dispatch('view/setLoadingMain', false);
}

function updateMeta(to) {
    if (to.meta && to.meta.title) {
        document.title = `${to.meta.title} | ${process.env.VUE_APP_SITENAME}`;
    }
    // プロダクション環境のみ
    if (process.env.VUE_APP_ENV === 'production') {
        let robots = 'noindex,nofollow';
        // ログインページのみインデックスされるようにする
        if (to.name === 'login') {
            robots = 'index,follow';
        }
        document.getElementById('robots').setAttribute('content', robots);
    }
}

function beforeEach(to, from, next) {
    // メンテナンスモードの場合はログインページに遷移させる
    if (store.getters['view/isMaintenance']) {
        if (to.name !== 'login') {
            handleNext({ name: 'login', replace: true }, from, next);
        } else {
            next();
        }
        return;
    }

    // デモモードの場合、デモで使えないページへは遷移できないようにする
    if (
        process.env.VUE_APP_ENV === 'demo' &&
        to.matched.some(record => record.meta.disableDemo)
    ) {
        next('/');
        return;
    }
    // 404に遷移
    if (
        to.name === '404' ||
        (!router.app.auth.isAuthenticated && to.name === 'project')
    ) {
        go404(from, next);
        return;
    }

    if (to.name === 'login') {
        // Auth0のエラーのクエリパラメータが付いていたら、エラーページに遷移
        // Auth0のメール認証系のエラー以外は/にクエリパラメータ付きでリダイレクトされてくる
        if (to.query.success === 'false') {
            handleNext(
                {
                    name: 'error',
                    query: { message: to.query.message },
                    replace: true
                },
                from,
                next
            );
            return;
        } else if (
            router.app.auth.isAuthenticated &&
            store.state.user.status.emailVerified
        ) {
            // ログイン状態でログインページに来たら、サイト一覧に遷移
            handleNext({ name: 'projects', replace: true }, from, next);
            return;
        }
    }

    if (to.matched.some(record => record.meta.requiresAuth)) {
        // ログアウト状態でログイン必須ページに来たら、ログインページに遷移
        if (!router.app.auth.isAuthenticated) {
            // ログイン後にリダイレクトさせるためのパスを保存しておく
            router.app.auth.setRedirectPath(to.fullPath);
            router.app.auth.logout();
            return;
        } else if (!store.state.user.status.emailVerified) {
            handleNext({ name: 'registerError', replace: true }, from, next);
            return;
        }

        if (process.env.VUE_APP_ENV !== 'demo') {
            if (store.state.user.status.isDisabled) {
                // 有効なアカウントではない場合は専用エラーページに遷移
                handleNext({ name: 'errorAccount', replace: true }, from, next);
                return;
            }
            // プラン情報を持っていない or 仮プランの時
            if (
                !store.state.user.plan.planId ||
                store.state.user.plan.targetService === 'kari'
            ) {
                if (!store.getters['user/hasProject']) {
                    // プラン情報もサイトもない場合は、サイト登録画面に遷移
                    // 代理店アカウントなどはサイト最大登録数が0の場合があるので除外する
                    if (
                        !to.matched.some(record => record.name === 'set') &&
                        !(
                            store.state.user.status.isAdmin ||
                            store.state.user.status.isAgency
                        )
                    ) {
                        handleNext({ name: 'set', replace: true }, from, next);
                        return;
                    }
                } else {
                    // プラン情報がなくてサイトがある場合はプラン選択画面に遷移
                    if (!to.matched.some(record => record.name === 'payment')) {
                        handleNext(
                            { name: 'paymentPlan', replace: true },
                            from,
                            next
                        );
                        return;
                    }
                    // 代理店ではないユーザーが、お支払い方法選択画面に来たらプラン選択画面に遷移
                    if (
                        !store.getters['user/isAgent'] &&
                        to.matched.some(
                            record => record.name === 'paymentSelect'
                        )
                    ) {
                        handleNext(
                            { name: 'paymentPlan', replace: true },
                            from,
                            next
                        );
                        return;
                    }
                }
            } else {
                // 仮プラン以外の時に、プラン選択画面に来たらサイト一覧に遷移
                if (to.matched.some(record => record.name === 'payment')) {
                    handleNext({ name: 'projects', replace: true }, from, next);
                    return;
                }
                // Stripe以外のプラン、もしくは代表者以外のユーザーが請求書画面に来たらサイト一覧に遷移
                if (
                    to.name === 'configPayment' &&
                    (store.state.user.plan.targetService !== 'stripe' ||
                        !store.getters['view/organization'].isDelegate)
                ) {
                    handleNext({ name: 'projects', replace: true }, from, next);
                    return;
                }
                // 組織権限を持つユーザー以外がアカウント管理画面に来たらサイト一覧に遷移
                if (
                    to.name === 'configManagement' &&
                    !store.getters['view/organization'].isAuthority
                ) {
                    handleNext({ name: 'projects', replace: true }, from, next);
                    return;
                }
            }
        }

        // 複数の組織に属しているユーザーの場合
        if (store.getters['user/hasOrganizations']) {
            if (to.matched.some(record => record.meta.requiresOrganization)) {
                // 存在する組織かどうかをチェック
                const existOrganization =
                    store.state.view.organizationId &&
                    store.getters['view/organization'].organizationId;
                if (!existOrganization) {
                    // お客様一覧(組織一覧)に遷移
                    handleNext(
                        { name: 'organization', replace: true },
                        from,
                        next
                    );
                    return;
                }
            }
        } else if (to.name === 'organization') {
            // 複数の組織に属していないユーザーがお客様一覧画面に来たらサイト一覧に遷移
            handleNext({ name: 'projects', replace: true }, from, next);
        }

        if (to.matched.some(record => record.meta.requiresProject)) {
            // 存在するサイトかどうかをチェック
            const existProject =
                to.params.projectId &&
                store.state.user.data.projects.some(
                    item => item.id === to.params.projectId
                );
            if (!existProject) {
                go404(from, next);
                return;
            } else if (to.name === 'project') {
                // /:projectIDにアクセスしてきたらGA・GSCデータサマリーに遷移
                handleNext(
                    { name: 'siteSummary', params: to.params, replace: true },
                    from,
                    next
                );
                return;
            }
        }
    }
    if (to.name === 'registerRegister') {
        // Auth0のエラーのクエリパラメータが付いていたら、エラーページに遷移
        if (to.query.success === 'false') {
            handleNext(
                {
                    name: 'registerError',
                    query: { message: to.query.message },
                    replace: true
                },
                from,
                next
            );
            return;
        } else if (!store.state.register.status.provisional) {
            // 新規登録ではなくメールアドレス変更でメール認証してきた場合は、ログインページに遷移
            handleNext({ name: 'login', replace: true }, from, next);
            return;
        }
    }
    // メールアドレス変更受付後のページで、queryパラメータにメールアドレスがない場合はログインページに遷移
    if (to.name === 'configUserProvisional') {
        if (!to.query.email) {
            handleNext({ name: 'login', replace: true }, from, next);
        }
    }
    // 競合キーワード分析の結果画面でサイトを移動する場合、一覧画面に戻す
    if (
        to.name === 'competitiveSiteKeywordResult' &&
        // リロード時はfrom.params.projectIdがundefinedになるため存在確認しておく
        from.params.projectId &&
        from.params.projectId !== to.params.projectId
    ) {
        handleNext(
            {
                name: 'competitiveSiteKeyword',
                params: to.params,
                replace: true
            },
            from,
            next
        );
    }
    setTimeout(next, 100);
}

router.beforeEach(async (to, from, next) => {
    // タイトルなどを更新
    updateMeta(to);
    // ローダー表示
    startLoading(from, to);

    if (!isInited) {
        isInited = true;

        // サイレント認証を行う
        await router.app.auth.renewTokens().catch(() => {});
        // 謹告を取得する
        await getNotifications();
        // 1分毎に謹告を取得する
        setInterval(getNotifications, NOTIFICATION_INTERVAL_DELAY);

        // ログインしているかつメンテナンスモードでない場合は、各種情報を取得する
        if (
            router.app.auth.isAuthenticated &&
            !store.getters['view/isMaintenance']
        ) {
            await Promise.all([
                getUserData(),
                store.dispatch('user/getPlan'),
                store.dispatch('user/getMe').finally(async () => {
                    // 有効なアカウントの場合はサイト一覧を取得する
                    if (!store.state.user.status.isDisabled) {
                        await store.dispatch('user/getProjects');
                    }
                })
            ]);
        }
    }
    beforeEach(to, from, next);
});

router.afterEach(async (to, from) => {
    // タイトルなどを更新
    updateMeta(to);
    store.dispatch('view/setProjectId', to.params.projectId);

    // お客様一覧(組織一覧)に遷移した場合は組織を選ぶ画面になるため、保持ているorganizationId,projectIdをリセットする
    if (to.name === 'organization') {
        store.dispatch('view/setOrganizationId', '');
        store.dispatch('view/setProjectId', '');
    }

    if (from.params.projectId !== to.params.projectId) {
        // サイトに依存する値をリセットする
        store.dispatch('project/reset');
    }
    stopLoading();

    // ログインしている場合のみ、値を設定する
    if (
        router.app.auth.isAuthenticated &&
        store.state.user.status.emailVerified
    ) {
        const idToken = router.app.auth.idToken;
        // 複数の組織に属しているユーザー(社員)の場合はローカルストレージにUsageのデータを追加
        // ユーザートラッキングのみで扱っている
        // 実処理で扱う場合は注意
        if (!store.getters['user/hasOrganizations']) {
            const organizationId =
                store.getters['view/organization'].organizationId;
            await Promise.all([
                tracking.setUsages(idToken),
                tracking.setStatuses(organizationId, idToken)
            ]);
        }

        const trackingData = await tracking.getTrackingData(
            router.app.auth.sub,
            to.params.projectId,
            idToken
        );
        if (trackingData) {
            gtm.pushAmplitudeTracking(trackingData);
        }
    }
});

export default router;
